import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getPatternById } from '../../data';
import { CategoryBadge } from '../../components/CategoryBadge/CategoryBadge';
import { ComplexityIndicator } from '../../components/ComplexityIndicator/ComplexityIndicator';
import { categoryColor, categorySlug, categoryBadgeVariant, ifTypeBadgeVariant } from '../../utils/categoryColors';
import { Category, Example, InterfaceType, PatternExample } from '../../types';
import { ExampleScene } from '../../components/ExampleScene/ExampleScene';
import { Badge } from '@wearenotch/ui-react';
import { DemoWrapper } from '../../components/DemoWrapper/DemoWrapper';
import { demos } from '../../demos';
import { useReveal } from '../../hooks/useReveal';
import styles from './PatternDetail.module.css';

const ifTabColor: Record<InterfaceType, string> = {
  'Chat UI':    '#0d3272',
  'Voice UI':   '#9a3fd4',
  'Agentic UI': '#d4691c',
};

const ifSuffix: Record<InterfaceType, string> = {
  'Chat UI':    'chat',
  'Voice UI':   'voice',
  'Agentic UI': 'agentic',
};

function ExampleCard({ example, kind, ifType }: { example: Example; kind: 'good' | 'bad'; ifType: InterfaceType }) {
  const good = kind === 'good';
  return (
    <div className={`${styles.example} ${good ? styles.good : styles.bad}`}>
      <div className={styles.exampleLabel}>
        {good ? (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        ) : (
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        )}
        {good ? 'Good example' : 'Bad example'}
      </div>
      <p className={styles.exampleText}>{example.summary}</p>
      {example.scene && example.scene.length > 0 && (
        <ExampleScene turns={example.scene} interfaceType={ifType} />
      )}
      <div className={styles.exampleWhy}>
        <span className={styles.whyLabel}>{good ? 'Why it works' : 'Why it fails'}</span>
        <p className={styles.exampleDetail}>{example.detail}</p>
      </div>
    </div>
  );
}

function ExamplePair({ ex, fallbackType }: { ex: PatternExample; fallbackType: InterfaceType }) {
  const ifType = ex.interface_type ?? fallbackType;
  return (
    <div className={styles.examples}>
      <ExampleCard example={ex.good} kind="good" ifType={ifType} />
      <ExampleCard example={ex.bad} kind="bad" ifType={ifType} />
    </div>
  );
}

export function PatternDetail() {
  const { id } = useParams<{ id: string }>();
  const pattern = id ? getPatternById(id) : undefined;

  const hasTypedTabs = (pattern?.examples.length ?? 0) > 1 &&
    pattern?.examples.every(e => e.interface_type);

  const [activeTabIndex, setActiveTabIndex] = useState(0);

  // Reset tab when pattern changes
  useEffect(() => { setActiveTabIndex(0); }, [id]);
  useReveal([id]);

  if (!pattern) {
    return (
      <main className={styles.main}>
        <div className="container">
          <div className={styles.notFound}>
            <h1>Pattern not found</h1>
            <Link to="/" className={styles.backLink}>← Back to home</Link>
          </div>
        </div>
      </main>
    );
  }

  const color = categoryColor[pattern.category];
  const slug = categorySlug[pattern.category as Category];

  // Resolve which demo to show based on active tab
  const activeExample = pattern.examples[activeTabIndex];
  const activeIfType = activeExample?.interface_type;
  const tabDemoKey = activeIfType
    ? `${pattern.id}-${ifSuffix[activeIfType]}`
    : pattern.id;
  const DemoComponent = demos[tabDemoKey] ?? demos[pattern.id];

  console.log('[PatternDetail] id:', pattern.id, '| tab:', activeIfType ?? 'none', '| demo:', tabDemoKey, '| found:', !!DemoComponent);

  return (
    <main className={styles.main}>
      <div className={styles.hero}>
        <div className="container">
          <nav className={styles.breadcrumbs}>
            <Link to="/" className={styles.crumb}>Home</Link>
            <span className={styles.sep}>/</span>
            <Link to={`/category/${slug}`} className={styles.crumb}>{pattern.category}</Link>
            <span className={styles.sep}>/</span>
            <span className={styles.crumbCurrent}>{pattern.title}</span>
          </nav>

          <div className={styles.heroContent}>
            <div className={styles.heroBadges}>
              <Badge variant={categoryBadgeVariant[pattern.category as Category]} size="sm">
                {pattern.category}
              </Badge>
              {pattern.interface_type?.map((t) => (
                <Badge key={t} variant={ifTypeBadgeVariant[t]} size="sm">{t}</Badge>
              ))}
            </div>
            <h1 className={`${styles.title} rise`}>{pattern.title}</h1>
          </div>
        </div>
      </div>

      <div className="container">
        <div className={styles.layout}>
          <article className={styles.article}>

            {/* Examples — tabbed or flat */}
            <section className={styles.section} data-reveal>
              <h2 className={styles.sectionTitle}>
                Examples
              </h2>

              {hasTypedTabs ? (
                <>
                  {/* Tab bar */}
                  <div className={styles.tabBar}>
                    {pattern.examples.map((ex, i) => {
                      const ifType = ex.interface_type!;
                      const isActive = i === activeTabIndex;
                      return (
                        <button
                          key={ifType}
                          className={`${styles.tab} ${isActive ? styles.tabActive : ''}`}
                          style={isActive ? { '--tab-color': ifTabColor[ifType] } as React.CSSProperties : undefined}
                          onClick={() => setActiveTabIndex(i)}
                        >
                          <Badge variant={ifTypeBadgeVariant[ifType]} size="sm">{ifType}</Badge>
                        </button>
                      );
                    })}
                  </div>
                  {/* Active example */}
                  <ExamplePair ex={pattern.examples[activeTabIndex]} fallbackType={pattern.interface_type[0]} />
                </>
              ) : (
                <div className={styles.exampleGroups}>
                  {pattern.examples.map((ex, i) => (
                    <ExamplePair key={i} ex={ex} fallbackType={pattern.interface_type[0]} />
                  ))}
                </div>
              )}
            </section>

            {/* Demo */}
            {DemoComponent && (
              <section className={styles.section} data-reveal>
                <h2 className={styles.sectionTitle}>
                  Try It
                </h2>
                <DemoWrapper key={tabDemoKey} title={pattern.title} chip={activeIfType ?? pattern.interface_type[0]}>
                  <DemoComponent />
                </DemoWrapper>
              </section>
            )}

            {/* Problem */}
            <section className={styles.section} data-reveal>
              <h2 className={styles.sectionTitle}>
                Problem
              </h2>
              <p className={styles.sectionText}>{pattern.problem}</p>
            </section>

            {/* When to use */}
            <section className={styles.section} data-reveal>
              <h2 className={styles.sectionTitle}>
                When to Use
              </h2>
              <p className={styles.sectionText}>{pattern.when_to_use}</p>
            </section>

            {/* Guidelines */}
            <section className={styles.section} data-reveal>
              <h2 className={styles.sectionTitle}>
                Guidelines
              </h2>
              <ul className={styles.guidelines}>
                {pattern.guidelines.map((g, i) => (
                  <li key={i} className={styles.guideline}>
                    <span className={styles.guidelineNumber} style={{ color }}>{String(i + 1).padStart(2, '0')}</span>
                    <span>{g}</span>
                  </li>
                ))}
              </ul>
            </section>

            {/* Related Patterns */}
            {pattern.related_patterns && pattern.related_patterns.length > 0 && (() => {
              const related = pattern.related_patterns
                .map(slug => getPatternById(slug))
                .filter((p): p is NonNullable<typeof p> => p !== undefined);
              if (related.length === 0) return null;
              return (
                <section className={styles.section} data-reveal>
                  <h2 className={styles.sectionTitle}>
                    Related Patterns
                  </h2>
                  <div className={styles.relatedGrid}>
                    {related.map(rel => (
                      <Link key={rel.id} to={`/pattern/${rel.id}`} className={styles.relatedCard}>
                        <CategoryBadge category={rel.category} />
                        <span className={styles.relatedTitle}>{rel.title}</span>
                        <p className={styles.relatedProblem}>{rel.problem}</p>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={styles.relatedArrow}>
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </Link>
                    ))}
                  </div>
                </section>
              );
            })()}
          </article>

          {/* Sidebar */}
          <aside className={styles.sidebar}>
            <div className={styles.sidebarCard}>
              <h3 className={styles.sidebarTitle}>About this pattern</h3>
              <dl className={styles.meta}>
                <dt>Category</dt>
                <dd>
                  <Link to={`/category/${slug}`} className={styles.categoryLink} style={{ color }}>
                    {pattern.category}
                  </Link>
                </dd>
                <dt>Guidelines</dt>
                <dd>{pattern.guidelines.length}</dd>
                {pattern.complexity && (
                  <>
                    <dt>Complexity</dt>
                    <dd><ComplexityIndicator complexity={pattern.complexity} /></dd>
                  </>
                )}
              </dl>
            </div>

            <Link to={`/category/${slug}`} className={styles.morePatterns}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 12H5M12 19l-7-7 7-7" />
              </svg>
              More {pattern.category} patterns
            </Link>
          </aside>
        </div>
      </div>
    </main>
  );
}
